import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import axios from "axios";

import * as XLSX from "xlsx";

import {
  FaCalendarAlt,
  FaChevronRight,
  FaFileExcel,
  FaMoneyBillWave,
  FaReceipt,
  FaSyncAlt,
  FaWallet,
} from "react-icons/fa";

import "./ProfitLossReport.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const getHeaders = () => {

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("access_token") ||
    "";


  const schoolId =
    localStorage.getItem("school_id") ||
    localStorage.getItem("schoolId") ||
    localStorage.getItem(
      "selected_school_id"
    ) ||
    "";


  return {

    Authorization:
      token
        ? `Bearer ${token}`
        : "",

    "X-School-Id":
      schoolId,

    Accept:
      "application/json",
  };
};


const money = (value) =>
  new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );


const ProfitLossReportPage = () => {

  const navigate =
    useNavigate();


  const [data, setData] =
    useState({

      academic_year:
        null,

      months:
        [],

      grand_total: {

        collection:
          0,

        salary_expense:
          0,

        other_expense:
          0,

        total_expense:
          0,

        net_profit_loss:
          0,

        result_type:
          "break_even",
      },
    });


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  /* ============================================================
     LOAD
  ============================================================ */

  const loadReport = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/profit-loss/monthly-summary`,

          {
            headers:
              getHeaders(),
          }
        );


      if (
        response?.data?.status
      ) {

        const report =
          response.data.data ||
          {};


        setData({

          academic_year:
            report.academic_year ||
            null,

          months:
            Array.isArray(
              report.months
            )
              ? report.months
              : [],

          grand_total:
            report.grand_total ||
            {
              collection: 0,
              salary_expense: 0,
              other_expense: 0,
              total_expense: 0,
              net_profit_loss: 0,
              result_type:
                "break_even",
            },
        });

      } else {

        setError(

          response?.data?.message ||
            "Unable to load P&L report."
        );
      }

    } catch (err) {

      console.error(
        "P&L report error:",
        err
      );


      setError(

        err?.response?.data
          ?.message ||

          "Unable to load P&L report."
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    loadReport();

  }, []);


  const months =
    useMemo(
      () =>
        Array.isArray(
          data.months
        )
          ? data.months
          : [],
      [data.months]
    );


  const totals =
    data.grand_total || {};


  /* ============================================================
     OPEN MONTH
  ============================================================ */

  const openMonth = (row) => {

    if (
      !row?.month ||
      !row?.is_accrued
    ) {

      return;
    }


    navigate(

      `/admin/reports/profit-loss/${row.month}`,

      {

        state: {

          monthName:
            `${row.month_name} ${row.year}`,
        },
      }
    );
  };


  /* ============================================================
     EXCEL
  ============================================================ */

  const exportExcel = () => {

    if (!months.length) {
      return;
    }


    const rows =
      months.map(
        (
          row,
          index
        ) => ({

          "#":
            index + 1,

          "Month":
            `${row.month_name} ${row.year}`,

          "Total Collection":
            Number(
              row.collection ||
              0
            ),

          "Payroll Salary":
            Number(
              row.payroll_salary ||
              0
            ),

          "Manual Salary":
            Number(
              row.manual_salary ||
              0
            ),

          "Total Salary":
            Number(
              row.salary_expense ||
              0
            ),

          "Other Expense":
            Number(
              row.other_expense ||
              0
            ),

          "Total Expense":
            Number(
              row.total_expense ||
              0
            ),

          "Net Profit / Loss":
            Number(
              row.net_profit_loss ||
              0
            ),

          "Result":
            !row.is_accrued
              ? "Upcoming"
              : row.result_type ===
                "profit"
                ? "Profit"
                : row.result_type ===
                    "loss"
                  ? "Loss"
                  : "Break Even",
        })
      );


    rows.push({

      "#": "",

      "Month":
        "GRAND TOTAL",

      "Total Collection":
        Number(
          totals.collection ||
          0
        ),

      "Payroll Salary":
        Number(
          totals.payroll_salary ||
          0
        ),

      "Manual Salary":
        Number(
          totals.manual_salary ||
          0
        ),

      "Total Salary":
        Number(
          totals.salary_expense ||
          0
        ),

      "Other Expense":
        Number(
          totals.other_expense ||
          0
        ),

      "Total Expense":
        Number(
          totals.total_expense ||
          0
        ),

      "Net Profit / Loss":
        Number(
          totals.net_profit_loss ||
          0
        ),

      "Result":
        totals.result_type ===
        "profit"
          ? "Profit"
          : totals.result_type ===
              "loss"
            ? "Loss"
            : "Break Even",
    });


    const worksheet =
      XLSX.utils.json_to_sheet(
        rows
      );


    worksheet["!cols"] = [

      { wch: 6 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 15 },

    ];


    const workbook =
      XLSX.utils.book_new();


    XLSX.utils
      .book_append_sheet(

        workbook,

        worksheet,

        "Profit Loss"
      );


    const year =
      String(

        data.academic_year
          ?.session_name ||

        "Academic_Year"

      ).replace(

        /[^a-zA-Z0-9_-]/g,

        "_"
      );


    XLSX.writeFile(

      workbook,

      `Profit_Loss_Report_${year}.xlsx`
    );
  };


  /* ============================================================
     UI
  ============================================================ */

  return (

    <div className="pl-page">


      {/* HEADER */}

      <div className="pl-page-header">


        <div className="pl-heading">


          <div className="pl-title-icon">

            <FaWallet />

          </div>


          <div>

            <h1>
              Profit & Loss Report
            </h1>

            <p>
              Monthly collection,
              expense and net
              profit/loss
            </p>

          </div>

        </div>


        <div className="pl-actions">


          <button
            type="button"
            className="pl-excel-btn"
            onClick={
              exportExcel
            }
            disabled={
              loading ||
              !months.length
            }
          >

            <FaFileExcel />

            Excel

          </button>


          <button
            type="button"
            className="pl-refresh-btn"
            onClick={
              loadReport
            }
            disabled={
              loading
            }
          >

            <FaSyncAlt
              className={
                loading
                  ? "pl-spin"
                  : ""
              }
            />

            Refresh

          </button>

        </div>

      </div>


      {/* ACADEMIC YEAR */}

      {data.academic_year && (

        <div className="pl-session-card">


          <div className="pl-session-icon">

            <FaCalendarAlt />

          </div>


          <div>

            <span>
              Academic Year
            </span>

            <strong>

              {
                data
                  .academic_year
                  .session_name
              }

            </strong>

          </div>


          <div className="pl-session-dates">

            {
              data
                .academic_year
                .start_date
            }

            <b>→</b>

            {
              data
                .academic_year
                .end_date
            }

          </div>

        </div>

      )}


      {/* SUMMARY */}

      <div className="pl-summary-grid">


        <div className="pl-summary-card">

          <div className="pl-summary-icon green">

            <FaMoneyBillWave />

          </div>

          <div>

            <span>
              Total Collection
            </span>

            <strong className="pl-green">

              {money(
                totals.collection
              )}

            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div className="pl-summary-icon orange">

            <FaReceipt />

          </div>

          <div>

            <span>
              Total Expense
            </span>

            <strong className="pl-orange">

              {money(
                totals.total_expense
              )}

            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div
            className={`pl-summary-icon ${
              Number(
                totals.net_profit_loss ||
                0
              ) >= 0
                ? "green"
                : "red"
            }`}
          >

            <FaWallet />

          </div>

          <div>

            <span>

              {Number(
                totals.net_profit_loss ||
                0
              ) >= 0
                ? "Net Profit"
                : "Net Loss"}

            </span>

            <strong
              className={
                Number(
                  totals.net_profit_loss ||
                  0
                ) >= 0
                  ? "pl-green"
                  : "pl-red"
              }
            >

              {money(
                totals.net_profit_loss
              )}

            </strong>

          </div>

        </div>

      </div>


      {error && (

        <div className="pl-error">

          {error}

        </div>

      )}


      {/* TABLE */}

      <div className="pl-card">


        <div className="pl-card-head">

          <div>

            <h2>
              Month Wise P&L
            </h2>

            <p>
              Each month is calculated
              separately. Previous
              month data is not
              carried forward.
            </p>

          </div>

        </div>


        {loading ? (

          <div className="pl-loading">

            <div className="pl-loader" />

            Loading P&L report...

          </div>

        ) : (

          <div className="pl-table-wrap">


            <table className="pl-table">


              <thead>

                <tr>

                  <th>
                    Month
                  </th>

                  <th className="right">
                    Collection
                  </th>

                  <th className="right">
                    Salary Expense
                  </th>

                  <th className="right">
                    Other Expense
                  </th>

                  <th className="right">
                    Total Expense
                  </th>

                  <th className="right">
                    Net Profit / Loss
                  </th>

                  <th className="center">
                    Action
                  </th>

                </tr>

              </thead>


              <tbody>


                {months.map(
                  (row) => {

                    const net =
                      Number(
                        row.net_profit_loss ||
                        0
                      );


                    return (

                      <tr
                        key={
                          row.month
                        }
                        className={
                          !row.is_accrued
                            ? "pl-future-row"
                            : ""
                        }
                      >

                        <td>

                          <div className="pl-month">


                            <span className="pl-month-box">

                              {
                                row.month_short
                              }

                            </span>


                            <div>

                              <strong>

                                {
                                  row.month_name
                                }

                              </strong>

                              <small>

                                {row.year}

                              </small>

                            </div>

                          </div>

                        </td>


                        <td className="right">

                          {row.is_accrued ? (

                            <strong className="pl-green">

                              {money(
                                row.collection
                              )}

                            </strong>

                          ) : (

                            <span className="pl-upcoming">

                              Upcoming

                            </span>

                          )}

                        </td>


                        <td className="right">

                          {row.is_accrued
                            ? money(
                                row.salary_expense
                              )
                            : money(0)}

                        </td>


                        <td className="right">

                          {row.is_accrued
                            ? money(
                                row.other_expense
                              )
                            : money(0)}

                        </td>


                        <td className="right">

                          <strong className="pl-orange">

                            {row.is_accrued
                              ? money(
                                  row.total_expense
                                )
                              : money(0)}

                          </strong>

                        </td>


                        <td className="right">

                          {row.is_accrued ? (

                            <strong
                              className={
                                net >= 0
                                  ? "pl-green"
                                  : "pl-red"
                              }
                            >

                              {money(net)}

                            </strong>

                          ) : (

                            <span className="pl-upcoming">
                              -
                            </span>

                          )}

                        </td>


                        <td className="center">

                          <button
                            type="button"
                            className="pl-view-btn"
                            disabled={
                              !row.is_accrued
                            }
                            onClick={() =>
                              openMonth(
                                row
                              )
                            }
                          >

                            View

                            <FaChevronRight />

                          </button>

                        </td>

                      </tr>

                    );
                  }
                )}


                {!months.length && (

                  <tr>

                    <td
                      colSpan="7"
                      className="pl-empty"
                    >

                      No P&L data found.

                    </td>

                  </tr>

                )}

              </tbody>


              {!!months.length && (

                <tfoot>

                  <tr>

                    <td>
                      GRAND TOTAL
                    </td>

                    <td className="right pl-total-green">

                      {money(
                        totals.collection
                      )}

                    </td>

                    <td className="right">

                      {money(
                        totals.salary_expense
                      )}

                    </td>

                    <td className="right">

                      {money(
                        totals.other_expense
                      )}

                    </td>

                    <td className="right pl-total-orange">

                      {money(
                        totals.total_expense
                      )}

                    </td>

                    <td
                      className={`right ${
                        Number(
                          totals.net_profit_loss ||
                          0
                        ) >= 0
                          ? "pl-total-green"
                          : "pl-total-red"
                      }`}
                    >

                      {money(
                        totals.net_profit_loss
                      )}

                    </td>

                    <td />

                  </tr>

                </tfoot>

              )}

            </table>

          </div>

        )}

      </div>

    </div>
  );
};


export default ProfitLossReportPage;